import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { useZero } from "@/hooks/use-zero";
import { useSession } from "@/features/auth/use-session";
import { useCollection } from "@/features/collections/use-collections";
import { useCollectionEntries } from "@/features/collections/use-collection-entries";
import { uploadOne } from "@/lib/upload";
import { createPhotoEntry } from "@/lib/mutators";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { UploadDropzone } from "@/components/lovd/UploadDropzone";
import { EntryThumbnail } from "@/components/lovd/EntryThumbnail";

export default function CollectionPage() {
  const { id } = useParams<{ id: string }>();
  const { data: session } = useSession();
  const z = useZero();
  const { collection } = useCollection(id);
  const { entries, type } = useCollectionEntries(id);

  const [uploading, setUploading] = useState<string | null>(null);

  async function handleFiles(files: File[]) {
    if (!session?.user || !id) return;
    try {
      for (const file of files) {
        setUploading(file.name);
        const { mediaId } = await uploadOne(file);
        await createPhotoEntry(z, session.user.id, id, mediaId, { title: file.name });
      }
      toast.success(`Uploaded ${files.length} photo${files.length === 1 ? "" : "s"}`);
    } catch (err) {
      console.error(err);
      toast.error("Upload failed", { description: String(err) });
    } finally {
      setUploading(null);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-8 py-10">
      <header className="mb-6 flex items-end justify-between">
        <div>
          <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2">
            <Link to="/collections">
              <ChevronLeft className="mr-1 h-4 w-4" />
              All collections
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">
            {collection?.name ?? "Loading…"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {type === "complete"
              ? `${entries.length} ${entries.length === 1 ? "photo" : "photos"}`
              : "Loading…"}
          </p>
        </div>
      </header>

      <UploadDropzone
        onFiles={handleFiles}
        className="mb-6"
        hint={
          uploading
            ? `Uploading ${uploading}…`
            : "Drop photos here or click to browse"
        }
      />

      {type === "unknown" ? (
        <Grid>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-lg" />
          ))}
        </Grid>
      ) : entries.length === 0 ? (
        <p className="mt-12 text-center text-sm text-muted-foreground">
          No photos in this collection yet.
        </p>
      ) : (
        <Grid>
          {entries.map((e) => (
            <EntryThumbnail key={e.id} entry={e} />
          ))}
        </Grid>
      )}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {children}
    </ul>
  );
}
